export const TAC_POINT_KINDS = ["flag", "base", "titan", "silo", "marker"] as const;
export type TacPointKind = (typeof TAC_POINT_KINDS)[number];

export const TAC_POINT_TEAMS = ["pac", "eu", "neutral"] as const;
export type TacPointTeam = (typeof TAC_POINT_TEAMS)[number];

export interface TacPoint {
  id: string;
  label: string;
  kind: TacPointKind;
  x: number;
  y: number;
  team?: TacPointTeam;
  flagName?: string;
  order?: number;
  enabled: boolean;
  /** Optionaler Map-Overlay-Icon-Pfad (z.B. Legacy-Static-Objects). Manuelle Punkte ohne Icon → CSS-Fallback. */
  icon?: string;
  /** Optionale Icon-Rotation in Grad (z.B. Titan-Ausrichtung aus Legacy `rot`). */
  rot?: number;
}

const KIND_SET = new Set<string>(TAC_POINT_KINDS);
const TEAM_SET = new Set<string>(TAC_POINT_TEAMS);

export function clamp01(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function fallbackId(index: number): string {
  return `marker-${index + 1}`;
}

export function sanitizeTacPoints(input: unknown): TacPoint[] {
  if (!Array.isArray(input)) return [];
  return input.map((raw, index) => {
    const p = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
    const id = String(p.id ?? "").trim() || fallbackId(index);
    const label = String(p.label ?? "").trim() || "Marker";
    const kindRaw = String(p.kind ?? "").trim();
    const teamRaw = String(p.team ?? "").trim();
    const flagName = String(p.flagName ?? "").trim();
    const order = Number(p.order);
    return {
      id,
      label,
      kind: (KIND_SET.has(kindRaw) ? kindRaw : "marker") as TacPointKind,
      x: clamp01(p.x),
      y: clamp01(p.y),
      ...(TEAM_SET.has(teamRaw) ? { team: teamRaw as TacPointTeam } : {}),
      ...(flagName ? { flagName } : {}),
      ...(Number.isFinite(order) ? { order } : {}),
      enabled: p.enabled === undefined ? true : !!p.enabled,
    };
  });
}

export function visibleTacPoints(points: TacPoint[]): TacPoint[] {
  return points.filter((p) => p.enabled !== false);
}

export function createTacPoint(x: number, y: number, index: number): TacPoint {
  return {
    id: fallbackId(index),
    label: "Marker",
    kind: "marker",
    x: clamp01(x),
    y: clamp01(y),
    team: "neutral",
    enabled: true,
  };
}

export function pointFromClientRect(clientX: number, clientY: number, rect: Pick<DOMRect, "left" | "top" | "width" | "height">): { x: number; y: number } {
  return {
    x: clamp01((clientX - rect.left) / Math.max(1, rect.width)),
    y: clamp01((clientY - rect.top) / Math.max(1, rect.height)),
  };
}

export function tacPointImageKey(src: string | null | undefined): string {
  return String(src ?? "").replace(/^\/+/, "");
}
